import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useEyeconxStore } from '../../../store/eyeconxStore';
import clinicService from '../../../services/clinic.service';
import SubHeader from '../../layout/components/SubHeader';
import Pagination from '../../common/Pagination';
import { markInvoiceSeen, markInvoicesSeen } from './invoiceSeenStorage';
import { countNewInvoices, INVOICE_QUERY, isNewInvoice } from './invoiceUtils';
import InvoicesSkeleton from './subparts/InvoicesSkeleton';
import './Invoices.css';

const PAGE_SIZE_OPTIONS = [10, 25, 50];

const formatFileSize = (bytes) => {
    if (!bytes) {
        return null;
    }

    const units = ['B', 'KB', 'MB', 'GB'];
    let size = bytes;
    let unitIndex = 0;

    while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
    }

    return `${size.toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
};

const getFileIcon = (fileName) => {
    const extension = fileName?.split('.').pop()?.toLowerCase();

    if (extension === 'pdf') {
        return 'bi-file-earmark-pdf';
    }

    if (['doc', 'docx'].includes(extension)) {
        return 'bi-file-earmark-word';
    }

    if (['jpg', 'jpeg', 'png'].includes(extension)) {
        return 'bi-file-earmark-image';
    }

    return 'bi-file-earmark-text';
};

function Invoices() {
    const clinicId = useEyeconxStore((state) => state.user?.clinicId);
    const location = useLocation();
    const trackedInvoiceIdsRef = useRef([]);
    const previousPathRef = useRef(location.pathname);

    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [pageNumber, setPageNumber] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(0);
    const [totalElements, setTotalElements] = useState(0);
    const [newInvoiceCount, setNewInvoiceCount] = useState(0);

    const refreshNewInvoiceCount = useCallback((invoiceList) => {
        if (!clinicId) {
            setNewInvoiceCount(0);
            return;
        }

        setNewInvoiceCount(countNewInvoices(invoiceList, clinicId));
    }, [clinicId]);

    const syncTrackedInvoices = useCallback(async () => {
        if (!clinicId) {
            trackedInvoiceIdsRef.current = [];
            setNewInvoiceCount(0);
            return;
        }

        try {
            const response = await clinicService.getDocuments(clinicId, 0, 100, INVOICE_QUERY);
            const content = response.data?.content || [];
            const invoiceIds = content.map((invoice) => invoice.id).filter(Boolean);

            trackedInvoiceIdsRef.current = invoiceIds;
            refreshNewInvoiceCount(content);
        } catch (error) {
            console.error('Failed to load invoice notification state', error);
        }
    }, [clinicId, refreshNewInvoiceCount]);

    const fetchInvoices = useCallback(async (page, size) => {
        if (!clinicId) {
            return;
        }

        setLoading(true);

        try {
            const response = await clinicService.getDocuments(clinicId, page, size, INVOICE_QUERY);
            const data = response.data;
            const content = data.content || [];

            setInvoices(content);
            setPageNumber(data.number ?? page);
            setPageSize(data.size ?? size);
            setTotalPages(data.totalPages ?? 0);
            setTotalElements(data.totalElements ?? 0);
        } catch (error) {
            console.error('Failed to load invoices', error);
            toast.error('Failed to load invoices');
        } finally {
            setLoading(false);
        }
    }, [clinicId]);

    useEffect(() => {
        fetchInvoices(pageNumber, pageSize);
    }, [fetchInvoices, pageNumber, pageSize]);

    useEffect(() => {
        syncTrackedInvoices();
    }, [syncTrackedInvoices]);

    useEffect(() => {
        const handleFocus = () => {
            syncTrackedInvoices();
            fetchInvoices(pageNumber, pageSize);
        };

        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, [syncTrackedInvoices, fetchInvoices, pageNumber, pageSize]);

    useEffect(() => {
        const previousPath = previousPathRef.current;

        if (previousPath === '/invoices' && location.pathname !== '/invoices' && clinicId) {
            markInvoicesSeen(clinicId, trackedInvoiceIdsRef.current);
        }

        previousPathRef.current = location.pathname;
    }, [location.pathname, clinicId]);

    useEffect(() => {
        const handlePageHide = () => {
            if (clinicId) {
                markInvoicesSeen(clinicId, trackedInvoiceIdsRef.current);
            }
        };

        window.addEventListener('pagehide', handlePageHide);
        return () => window.removeEventListener('pagehide', handlePageHide);
    }, [clinicId]);

    const handlePageSizeChange = (event) => {
        setPageSize(Number(event.target.value));
        setPageNumber(0);
    };

    const handleDownload = async (invoice) => {
        if (!clinicId || !invoice?.id) {
            return;
        }

        setDownloadingId(invoice.id);
        const wasNew = isNewInvoice(invoice, clinicId);

        try {
            const response = await clinicService.getDocumentById(clinicId, invoice.id);
            const blob = new Blob([response.data], { type: response.headers['content-type'] });
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = invoice.fileName || 'invoice';
            link.click();
            window.URL.revokeObjectURL(url);

            markInvoiceSeen(clinicId, invoice.id);
            if (wasNew) {
                setNewInvoiceCount((current) => Math.max(0, current - 1));
            }
        } catch (error) {
            console.error('Failed to download invoice', error);
            toast.error('Failed to download invoice');
        } finally {
            setDownloadingId(null);
        }
    };

    const description = totalElements
        ? `${totalElements} invoice${totalElements === 1 ? '' : 's'} available for download`
        : 'View and download clinic invoices';

    return (
        <div className="ecx-page ecx-page--full-width">
            <SubHeader
                title="Invoices"
                description={description}
                icon="bi-receipt"
                containerClass="w-100 px-4"
            />

            <div className="ecx-page-body">
                <div className="ecx-page-card ecx-page-panel">
                    <div className="ecx-page-panel__header">
                        <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 w-100">
                            <div className="d-flex flex-wrap align-items-center gap-2">
                                <h5 className="mb-0 fw-normal text-dark">Clinic Invoices</h5>
                                <span className="ecx-page-chip">
                                    <i className="bi bi-receipt" />
                                    {totalElements} total
                                </span>
                                {newInvoiceCount > 0 && (
                                    <span className="ecx-invoice-new-badge">
                                        <i className="bi bi-circle-fill" style={{ fontSize: '0.4rem' }} aria-hidden="true" />
                                        {newInvoiceCount} new
                                    </span>
                                )}
                            </div>

                            <label className="d-flex align-items-center gap-2 mb-0 small text-muted">
                                Show
                                <select
                                    className="form-select form-select-sm"
                                    value={pageSize}
                                    onChange={handlePageSizeChange}
                                    disabled={loading}
                                >
                                    {PAGE_SIZE_OPTIONS.map((size) => (
                                        <option key={size} value={size}>
                                            {size}
                                        </option>
                                    ))}
                                </select>
                                entries
                            </label>
                        </div>
                    </div>

                    <div className="ecx-page-panel__body d-flex flex-column">
                        {loading ? (
                            <InvoicesSkeleton count={pageSize > 5 ? 5 : pageSize} />
                        ) : invoices.length === 0 ? (
                            <div className="ecx-page-empty py-5">
                                <span className="ecx-page-empty__icon">
                                    <i className="bi bi-receipt" />
                                </span>
                                <h5 className="mb-2 text-dark">No invoices yet</h5>
                                <p className="mb-0 text-muted">
                                    Clinic invoices will appear here when they are available.
                                </p>
                            </div>
                        ) : (
                            <div className="d-flex flex-column gap-2">
                                {newInvoiceCount > 0 && (
                                    <div className="ecx-invoice-new-banner" role="status">
                                        <i className="bi bi-stars" aria-hidden="true" />
                                        <span>
                                            {newInvoiceCount} new invoice{newInvoiceCount === 1 ? '' : 's'} available to review
                                        </span>
                                    </div>
                                )}
                                {invoices.map((invoice) => {
                                    const isDownloading = downloadingId === invoice.id;
                                    const fileSize = formatFileSize(invoice.fileSize);
                                    const isNew = isNewInvoice(invoice, clinicId);

                                    return (
                                        <article
                                            key={invoice.id}
                                            className={`ecx-page-list-item d-flex align-items-center gap-3 p-3${isNew ? ' ecx-invoice-list-item--new' : ''}`}
                                        >
                                            <span className="ecx-page-icon-badge flex-shrink-0">
                                                <i className={`bi ${getFileIcon(invoice.fileName)}`} />
                                            </span>
                                            <div className="min-width-0 flex-grow-1">
                                                <div className="fw-normal text-dark text-truncate" title={invoice.fileName}>
                                                    {invoice.fileName}
                                                    {isNew && (
                                                        <span className="ecx-invoice-new-badge ms-2">
                                                            <i className="bi bi-circle-fill" style={{ fontSize: '0.4rem' }} aria-hidden="true" />
                                                            New
                                                        </span>
                                                    )}
                                                </div>
                                                <div className="small text-muted">
                                                    {invoice.createdAt || '—'}
                                                    {fileSize ? ` · ${fileSize}` : ''}
                                                </div>
                                            </div>
                                            <button
                                                type="button"
                                                className="btn btn-sm btn-outline-primary flex-shrink-0"
                                                onClick={() => handleDownload(invoice)}
                                                disabled={isDownloading}
                                            >
                                                {isDownloading ? (
                                                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                                                ) : (
                                                    <>
                                                        <i className="bi bi-download me-1" />
                                                        Download
                                                    </>
                                                )}
                                            </button>
                                        </article>
                                    );
                                })}
                            </div>
                        )}
                    </div>

                    {!loading && totalPages > 1 && (
                        <div className="ecx-page-panel__footer border-top px-3 py-3">
                            <Pagination
                                currentPage={pageNumber}
                                totalPages={totalPages}
                                onPageChange={setPageNumber}
                            />
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Invoices;
